import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import ResponsiveChart, { METRIC_COLOR_MAP } from '../components/ResponsiveChart';
import { PageHeader } from '../components/ui';
import { ExpandChevron, ExpandableRow } from '../components/ui/ExpandableRow';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { exportToExcel, ExcelColumn } from '../utils/exportToExcel';
import { toDateInputValue, todayDateInputValue } from '../utils/dateInput';

interface AgingMaintenanceRecord {
  id: number;
  aging_id: number;
  aging_start_date?: string;
  vessel_type?: string;
  ph?: number;
  ta?: number;
  color?: string;
  nose?: string;
  bisulfite?: number;
  so2?: number;
  topping?: boolean;
  topping_amount?: number;
  humidity?: number;
  flavor?: string;
  reading_timestamp: string;
  taken_by_id: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  aging?: {
    id: number;
    batch_id: number;
    vessel_id: number;
    start_date: string;
    end_date?: string;
    status: string;
    batch?: {
      id: number;
      batch_id: string;
      batch_name: string;
      wine_type?: string;
      start_date?: string;
      vineyard?: {
        id: number;
        name: string;
      };
    };
    vessel?: {
      id: number;
      vessel_id: string;
      name: string;
      type: string;
      capacity: number;
      capacity_unit: string;
    }
  };
  takenBy?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface AgingMaintenanceFormValues {
  aging_id: number | null;
  reading_timestamp?: string;
  aging_start_date?: string | null;
  ph?: number | null;
  ta?: number | null;
  color?: string | null;
  nose?: string | null;
  bisulfite?: number | null;
  so2?: number | null;
  topping?: boolean | null;
  topping_amount?: number | null;
  humidity?: number | null;
  flavor?: string | null;
  notes?: string;
}

const validationSchema = Yup.object({
  aging_id: Yup.number().required('Aging process is required').typeError('Please select an aging process'),
  reading_timestamp: Yup.date().required('Reading date is required'),
  aging_start_date: Yup.date().nullable(),
  ph: Yup.number().nullable(),
  ta: Yup.number().nullable(),
  color: Yup.string().nullable(),
  nose: Yup.string().nullable(),
  bisulfite: Yup.number().nullable(),
  so2: Yup.number().nullable(),
  topping: Yup.boolean().nullable(),
  topping_amount: Yup.number().nullable().when('topping', {
    is: true,
    then: (schema) => schema.min(0, 'Amount must be positive'),
    otherwise: (schema) => schema.nullable()
  }),
  humidity: Yup.number().nullable().min(0, 'Humidity must be between 0-100').max(100, 'Humidity must be between 0-100'),
  flavor: Yup.string().nullable(),
  notes: Yup.string()
});

// Mapping function to normalize data from the API
const mapApiDataToMaintenanceRecord = (apiData: any): AgingMaintenanceRecord => {
  console.log('Raw API data being mapped:', apiData);

  // Basic mapping for top-level fields
  const mapped: AgingMaintenanceRecord = {
    id: apiData.id,
    aging_id: apiData.aging_id || apiData.agingId,
    aging_start_date: apiData.aging_start_date || apiData.agingStartDate,
    vessel_type: apiData.vessel_type || apiData.vesselType,
    ph: apiData.ph,
    ta: apiData.ta,
    color: apiData.color,
    nose: apiData.nose,
    bisulfite: apiData.bisulfite,
    so2: apiData.so2,
    topping: apiData.topping,
    topping_amount: apiData.topping_amount || apiData.toppingAmount,
    humidity: apiData.humidity,
    flavor: apiData.flavor,
    reading_timestamp: apiData.reading_timestamp || apiData.readingTimestamp || new Date().toISOString(),
    taken_by_id: apiData.taken_by_id || apiData.takenById,
    notes: apiData.notes,
    created_at: apiData.created_at || apiData.createdAt || new Date().toISOString(),
    updated_at: apiData.updated_at || apiData.updatedAt || new Date().toISOString()
  };

  // Map aging relationship
  if (apiData.aging) {
    console.log('Using direct aging data from API response');
    mapped.aging = {
      id: apiData.aging.id,
      batch_id: apiData.aging.batch_id || apiData.aging.batchId,
      vessel_id: apiData.aging.vessel_id || apiData.aging.vesselId,
      start_date: apiData.aging.start_date || apiData.aging.startDate,
      end_date: apiData.aging.end_date || apiData.aging.endDate,
      status: apiData.aging.status
    };

    // Map batch within aging
    if (apiData.aging.batch) {
      console.log('Found batch in aging:', apiData.aging.batch);
      mapped.aging.batch = {
        id: apiData.aging.batch.id,
        batch_id: apiData.aging.batch.batch_id || apiData.aging.batch.batchId || `#${apiData.aging.batch.id}`,
        batch_name: apiData.aging.batch.batch_name || apiData.aging.batch.batchName || 'Unnamed Batch',
        wine_type: apiData.aging.batch.wine_type || apiData.aging.batch.wineType,
        start_date: apiData.aging.batch.start_date || apiData.aging.batch.startDate
      };

      // Map vineyard within batch
      if (apiData.aging.batch.vineyard) {
        console.log('Found vineyard in aging.batch:', apiData.aging.batch.vineyard);
        mapped.aging.batch.vineyard = {
          id: apiData.aging.batch.vineyard.id,
          name: apiData.aging.batch.vineyard.name
        };
      } else {
        console.log('No vineyard data in aging.batch');
      }
    } else {
      console.log('No batch data in aging');
    }

    // Map vessel within aging
    if (apiData.aging.vessel) {
      console.log('Found vessel in aging:', apiData.aging.vessel);
      mapped.aging.vessel = {
        id: apiData.aging.vessel.id,
        vessel_id: apiData.aging.vessel.vessel_id || apiData.aging.vessel.vesselId,
        name: apiData.aging.vessel.name,
        type: apiData.aging.vessel.type,
        capacity: apiData.aging.vessel.capacity,
        capacity_unit: apiData.aging.vessel.capacity_unit || apiData.aging.vessel.capacityUnit
      };
    } else {
      console.log('No vessel data in aging');
    }
  } else {
    console.log('No aging data available in API response');
  }

  // Map takenBy user
  if (apiData.takenBy) {
    mapped.takenBy = {
      id: apiData.takenBy.id,
      first_name: apiData.takenBy.first_name || apiData.takenBy.firstName,
      last_name: apiData.takenBy.last_name || apiData.takenBy.lastName,
      email: apiData.takenBy.email
    };
  }

  console.log('Mapped maintenance record:', mapped);
  return mapped;
};

// Helper function to format batch display
const formatBatchDisplay = (batch: any): { title: string; subtitle: string } => {
  if (!batch) {
    return { title: 'No ID', subtitle: 'Unnamed Batch' };
  }

  console.log('Batch data for formatting:', batch);

  // Try different possible batch identifier fields
  const batchId = batch.batch_id || batch.batchId || batch.id || `Batch #${batch.id}`;
  const batchName = batch.batch_name || batch.batchName || batch.name || 'Unnamed Batch';
  const vineyardName = batch.vineyard?.name;

  if (vineyardName) {
    return {
      title: `${vineyardName} - ${batchId}`,
      subtitle: batchName
    };
  }

  return {
    title: batchId,
    subtitle: batchName
  };
};

// Helper function to format vessel display
const formatVesselDisplay = (vessel: any): string => {
  if (!vessel) {
    return 'No vessel assigned';
  }

  // Try different possible vessel identifier fields
  const vesselId = vessel.vessel_id || vessel.vesselId;
  const vesselName = vessel.name;
  const vesselType = vessel.type;

  if (vesselId && vesselName) {
    return `${vesselId} - ${vesselName}`;
  }

  if (vesselId) {
    return vesselId;
  }

  if (vesselName) {
    return vesselName;
  }

  if (vesselType) {
    return `${vesselType} #${vessel.id}`;
  }

  return `Vessel #${vessel.id}`;
};

const AgingMaintenance: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';
  const [searchParams] = useSearchParams();
  const agingIdParam = searchParams.get('agingId');

  const [maintenanceRecords, setMaintenanceRecords] = useState<AgingMaintenanceRecord[]>([]);
  const [agingProcesses, setAgingProcesses] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<AgingMaintenanceRecord | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedAgingId, setSelectedAgingId] = useState<number | null>(agingIdParam ? parseInt(agingIdParam) : null);
  const [expandedRecords, setExpandedRecords] = useState<number[]>([]);
  const [visibleMetrics, setVisibleMetrics] = useState<string[]>(['ph', 'ta']);
  const [isChartOpen, setIsChartOpen] = useState<boolean>(true);
  const [showToppingAmount, setShowToppingAmount] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch aging processes with proper includes (similar to fermentation maintenance fetching batches)
        const agingRes = await api.get('/api/aging?include=batch.vineyard,vessel');
        console.log('Fetched aging processes:', agingRes.data);
        console.log('First aging process vessel data:', agingRes.data[0]?.vessel);
        console.log('First aging process batch data:', agingRes.data[0]?.batch);
        console.log('Complete first aging process:', JSON.stringify(agingRes.data[0], null, 2));
        setAgingProcesses(agingRes.data);

        // Fetch maintenance records (either all or filtered by aging id)
        let recordsRes;
        if (selectedAgingId) {
          recordsRes = await api.get(`/api/aging-maintenance/aging/${selectedAgingId}?include=aging.batch.vineyard,aging.vessel,takenBy`);
        } else {
          recordsRes = await api.get('/api/aging-maintenance?include=aging.batch.vineyard,aging.vessel,takenBy');
        }

        if (recordsRes.data) {
          console.log('Fetched maintenance records with data:', recordsRes.data);

          // Process the data through our mapping function
          const recordsData = Array.isArray(recordsRes.data)
            ? recordsRes.data
            : [recordsRes.data];

          const mappedRecords = recordsData.map(mapApiDataToMaintenanceRecord);
          console.log('Mapped maintenance records:', mappedRecords);

          setMaintenanceRecords(mappedRecords);
          setError(null);
        } else {
          console.error('No data returned from API');
          setError('No data returned from server');
        }
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchData();
  }, [selectedAgingId, tabVisible]);

  const openModal = (record: AgingMaintenanceRecord | null = null) => {
    setEditingRecord(record);
    setIsModalOpen(true);
    setShowToppingAmount(record?.topping === true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    setShowToppingAmount(false);
  };

  const handleSubmit = async (values: AgingMaintenanceFormValues, { resetForm, setSubmitting }: any) => {
    try {
      let updatedRecord: AgingMaintenanceRecord;

      if (editingRecord) {
        // Update existing record
        await api.put(`/api/aging-maintenance/${editingRecord.id}`, values);
        // Get the updated data with all associations
        updatedRecord = await getUpdatedRecord(editingRecord.id);
        setMaintenanceRecords(maintenanceRecords.map(r => (r.id === editingRecord.id ? updatedRecord : r)));
        setSuccessMessage('Maintenance record updated successfully!');
      } else {
        // Create new record
        const response = await api.post('/api/aging-maintenance', values);
        // Get the new data with all associations
        updatedRecord = await getUpdatedRecord(response.data.id);
        setMaintenanceRecords([updatedRecord, ...maintenanceRecords]);
        setSuccessMessage('Maintenance record created successfully!');
      }

      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving maintenance record:', err);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Get maintenance record data with associations
  const getUpdatedRecord = async (recordId: number) => {
    try {
      const response = await api.get(`/api/aging-maintenance/${recordId}?include=aging.batch.vineyard,aging.vessel,takenBy`);
      if (response.data) {
        console.log('Raw updated record:', response.data);
        // Apply the mapping function to normalize the data
        const mappedRecord = mapApiDataToMaintenanceRecord(response.data);
        console.log('Mapped updated record:', mappedRecord);
        return mappedRecord;
      } else {
        throw new Error('No data returned from server');
      }
    } catch (error: any) {
      console.error('Error fetching updated maintenance record:', error);
      throw new Error(`Failed to retrieve updated record: ${error.message || 'Unknown error'}`);
    }
  };

  const toggleExpand = (recordId: number) => {
    if (expandedRecords.includes(recordId)) {
      setExpandedRecords(expandedRecords.filter(id => id !== recordId));
    } else {
      setExpandedRecords([...expandedRecords, recordId]);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this maintenance record? This action cannot be undone.')) {
      try {
        await api.delete(`/api/aging-maintenance/${id}`);
        setMaintenanceRecords(maintenanceRecords.filter(record => record.id !== id));
        setSuccessMessage('Maintenance record deleted successfully!');

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      } catch (err: any) {
        console.error('Error deleting maintenance record:', err);
        setError(err);
      }
    }
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return 'Unknown date';
    try {
      console.log('Formatting timestamp:', timestamp);
      const date = new Date(timestamp);

      if (isNaN(date.getTime())) {
        console.error('Invalid date:', timestamp);
        return 'Invalid date';
      }

      // Format: "Apr 19, 2025, 10:30 AM"
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      }).format(date);
    } catch (e) {
      console.error('Error formatting timestamp:', e);
      return 'Invalid date';
    }
  };

  const hasMeasurement = (value: any) => {
    return value !== null && value !== undefined;
  };

  const handleAgingFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedAgingId(value ? parseInt(value) : null);
  };

  // Export aging maintenance records to Excel (desktop dataset).
  const handleExportToExcel = () => {
    if (!maintenanceRecords.length) return;

    const columns: ExcelColumn<AgingMaintenanceRecord>[] = [
      {
        header: 'Wine Batch',
        accessor: (r) => formatBatchDisplay(r.aging?.batch).title,
      },
      {
        header: 'Batch Name',
        accessor: (r) => formatBatchDisplay(r.aging?.batch).subtitle,
      },
      {
        header: 'Vessel',
        accessor: (r) =>
          r.aging?.vessel
            ? formatVesselDisplay(r.aging.vessel)
            : r.vessel_type || 'Not available',
      },
      {
        header: 'Reading Time',
        accessor: (r) => r.reading_timestamp,
      },
      {
        header: 'pH',
        accessor: (r) => (r.ph != null ? r.ph : ''),
      },
      {
        header: 'TA',
        accessor: (r) => (r.ta != null ? r.ta : ''),
      },
      {
        header: 'Bisulfite',
        accessor: (r) => (r.bisulfite != null ? r.bisulfite : ''),
      },
      {
        header: 'SO2',
        accessor: (r) => (r.so2 != null ? r.so2 : ''),
      },
    ];

    exportToExcel({
      fileName: `aging-maintenance-${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`,
      sheetName: 'AgingMaintenance',
      rows: maintenanceRecords,
      columns,
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Aging Maintenance"
        subtitle="Track and manage aging maintenance records."
        variant="compact"
        actions={(
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleExportToExcel}
            className="inline-flex items-center px-4 py-2 border border-monday-border text-sm font-medium rounded-md shadow-sm text-monday-text bg-white hover:bg-monday-hover"
          >
            Export to Excel
          </button>
          <PermissionButton
            resource="agingMaintenance"
            action="create"
            onClick={() => openModal()}
          >
            Add New Record
          </PermissionButton>
        </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      <div className="mt-4">
        <label htmlFor="aging-filter" className="form-label">
          Filter by Aging Process:
        </label>
        <div className="mt-1 relative rounded-md shadow-sm">
          <select
            id="aging-filter"
            name="aging-filter"
            className="focus:ring-primary-200 focus:border-primary-500 block w-full pl-3 pr-10 py-2 text-base border-monday-border focus:outline-none sm:text-sm rounded-md"
            value={selectedAgingId || ''}
            onChange={handleAgingFilterChange}
          >
            <option value="">All Aging Processes</option>
            {agingProcesses.map((aging) => (
              <option key={aging.id} value={aging.id}>
                {formatBatchDisplay(aging.batch).title} ({formatVesselDisplay(aging.vessel)})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Metrics selector and Time Series Chart */}
      <div className="mt-8 grid grid-cols-1 gap-4">
        <div className="bg-white/95 rounded-xl shadow border border-monday-border-light p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium text-monday-text">Measurements Over Time</h3>
              <p className="text-sm text-monday-text-muted">Timeline reflects current filters</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsChartOpen(prev => !prev)}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text hover:bg-monday-hover"
                aria-expanded={isChartOpen}
                aria-controls="aging-chart-section"
              >
                {isChartOpen ? 'Collapse' : 'Expand'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'ph', label: 'pH' },
                { key: 'ta', label: 'TA' },
                { key: 'bisulfite', label: 'Bisulfite' },
                { key: 'so2', label: 'SO2' },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() =>
                    setVisibleMetrics((prev) =>
                      prev.includes(m.key)
                        ? prev.filter((k) => k !== m.key)
                        : [...prev, m.key]
                    )
                  }
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${visibleMetrics.includes(m.key)
                    ? 'bg-primary-50 text-primary-600 border-primary-200'
                    : 'bg-white text-monday-text border-monday-border hover:bg-monday-hover'
                    }`}
                >
                  {m.label}
                </button>
              ))}
              <button
                onClick={() => setVisibleMetrics([])}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text-muted hover:bg-monday-hover"
              >
                Hide all
              </button>
              <button
                onClick={() => setVisibleMetrics(['ph', 'ta'])}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text-muted hover:bg-monday-hover"
              >
                Core
              </button>
            </div>
          </div>

          {isChartOpen && (() => {
            const records = maintenanceRecords.slice().sort((a, b) => {
              return new Date(a.reading_timestamp).getTime() - new Date(b.reading_timestamp).getTime();
            });
            const labels = records.map((r) => formatTimestamp(r.reading_timestamp));

            const metricDefs: Record<string, { label: string; color: string; getter: (r: any) => number | null }> = {
              ph: { label: 'pH', color: METRIC_COLOR_MAP.ph, getter: (r) => (r.ph ?? null) },
              ta: { label: 'TA', color: METRIC_COLOR_MAP.ta, getter: (r) => (r.ta ?? null) },
              bisulfite: { label: 'Bisulfite', color: METRIC_COLOR_MAP.bisulfite, getter: (r) => (r.bisulfite ?? null) },
              so2: { label: 'SO2', color: METRIC_COLOR_MAP.totalSo2, getter: (r) => (r.so2 ?? null) },
            };

            const datasets = visibleMetrics
              .filter((k) => metricDefs[k])
              .map((k) => {
                const def = metricDefs[k];
                return {
                  label: def.label,
                  data: records.map((r) => def.getter(r)),
                  borderColor: def.color,
                  backgroundColor: def.color + '0D',
                } as any;
              });

            const nonEmptyDatasets = datasets.filter((ds: any) => Array.isArray(ds.data) && ds.data.some((v: any) => v !== null && Number.isFinite(Number(v))));

            return (
              <div className="mt-4" id="aging-chart-section">
                {nonEmptyDatasets.length > 0 ? (
                  <ResponsiveChart
                    type="line"
                    title={selectedAgingId ? 'Aging Measurements' : 'All Measurements'}
                    data={{ labels, datasets: nonEmptyDatasets } as any}
                    height="360px"
                    variant="embedded"
                    instanceKey={`aging-${selectedAgingId || 'all'}-${visibleMetrics.join(',')}-${records.length}`}
                  />
                ) : (
                  <div className="bg-white rounded-xl shadow border border-monday-border-light p-8 text-center">
                    <svg className="mx-auto h-12 w-12 text-monday-status-neutral mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-monday-text font-medium text-sm mb-1">No data to display</p>
                    <p className="text-monday-text-muted text-xs">Select metrics above or add records to see insights</p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden sm:block">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-monday-border-light sm:rounded-lg">
                <table className="min-w-full divide-y divide-monday-border-light">
                  <thead className="bg-monday-header">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Wine Batch</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Vessel</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Date & Time</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Measurements</th>
                      <th scope="col" className="relative px-6 py-3"><span className="sr-only">Edit</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-monday-border-light bg-white">
                    {maintenanceRecords.length > 0 ? (
                      maintenanceRecords.map((record) => (
                        <React.Fragment key={record.id}>
                          <tr>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                              <div className="flex flex-col">
                                <span className="font-medium">{formatBatchDisplay(record.aging?.batch).title}</span>
                                <span className="text-monday-text-muted">{formatBatchDisplay(record.aging?.batch).subtitle}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                              {record.aging?.vessel ? (
                                <div className="flex flex-col">
                                  <span className="font-medium">{formatVesselDisplay(record.aging.vessel)}</span>
                                  <span>{record.vessel_type || record.aging.vessel.type || 'No type'}</span>
                                </div>
                              ) : (
                                record.vessel_type || "Not available"
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">{formatTimestamp(record.reading_timestamp)}</td>
                            <td className="px-3 py-4 text-sm text-monday-text-muted">
                              <ExpandChevron isExpanded={expandedRecords.includes(record.id)} onClick={(e) => { e.stopPropagation(); toggleExpand(record.id); }} />
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted text-right space-x-2">
                              <PermissionButton
                                resource="agingMaintenance"
                                action="update"
                                onClick={() => openModal(record)}
                                variant="secondary"
                                className="!px-2 !py-1 text-sm"
                              >
                                Edit
                              </PermissionButton>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDelete(record.id)}
                                  className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 border border-red-200"
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                          <ExpandableRow isExpanded={expandedRecords.includes(record.id)} colSpan={5}>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <h4 className="font-medium text-monday-text">Basic Information</h4>
                                    <div className="mt-2 space-y-2">
                                      {hasMeasurement(record.aging_start_date) && (
                                        <div className="text-sm">
                                          <span className="font-medium text-monday-text">Aging Start Date: </span>
                                          <span className="text-monday-text">{new Intl.DateTimeFormat('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                          }).format(new Date(record.aging_start_date!))}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-monday-text">Chemical Analysis</h4>
                                    <div className="mt-2 space-y-2">
                                      {hasMeasurement(record.ph) && (<div className="text-sm"><span className="font-medium text-monday-text">pH: </span><span className="text-monday-text">{record.ph}</span></div>)}
                                      {hasMeasurement(record.ta) && (<div className="text-sm"><span className="font-medium text-monday-text">TA: </span><span className="text-monday-text">{record.ta}</span></div>)}
                                      {hasMeasurement(record.bisulfite) && (<div className="text-sm"><span className="font-medium text-monday-text">Bisulfite: </span><span className="text-monday-text">{record.bisulfite}</span></div>)}
                                      {hasMeasurement(record.so2) && (<div className="text-sm"><span className="font-medium text-monday-text">SO2: </span><span className="text-monday-text">{record.so2}</span></div>)}
                                    </div>
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-monday-text">Sensory Analysis</h4>
                                    <div className="mt-2 space-y-2">
                                      {hasMeasurement(record.color) && (<div className="text-sm"><span className="font-medium text-monday-text">Color: </span><span className="text-monday-text">{record.color}</span></div>)}
                                      {hasMeasurement(record.nose) && (<div className="text-sm"><span className="font-medium text-monday-text">Nose: </span><span className="text-monday-text">{record.nose}</span></div>)}
                                      {hasMeasurement(record.flavor) && (<div className="text-sm"><span className="font-medium text-monday-text">Flavor: </span><span className="text-monday-text">{record.flavor}</span></div>)}
                                      {hasMeasurement(record.notes) && (<div className="text-sm"><span className="font-medium text-monday-text">Notes: </span><span className="text-monday-text">{record.notes}</span></div>)}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-medium text-monday-text">Maintenance Details</h4>
                                    <div className="mt-2 space-y-2">
                                      {record.topping !== undefined && record.topping !== null && (
                                        <div className="text-sm">
                                          <span className="font-medium text-monday-text">Topping: </span>
                                          <span className="text-monday-text">{record.topping ? `Yes${record.topping_amount ? ` - ${record.topping_amount} liters` : ''}` : 'No'}</span>
                                        </div>
                                      )}
                                      {hasMeasurement(record.humidity) && (<div className="text-sm"><span className="font-medium text-monday-text">Humidity: </span><span className="text-monday-text">{record.humidity}%</span></div>)}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-4 flex justify-end">
                                  <button onClick={() => handleDelete(record.id)} className="text-red-600 hover:text-red-900 text-sm font-medium">Delete Record</button>
                                </div>
                          </ExpandableRow>
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 text-center text-sm text-monday-text-muted">
                          No maintenance records found. {' '}
                          <button onClick={() => openModal()} className="text-primary-500 hover:text-primary-600">Add one now</button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="sm:hidden space-y-4">
          {maintenanceRecords.length > 0 ? (
            maintenanceRecords.map((record) => (
              <div key={record.id} className="bg-white shadow rounded-lg p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-base text-monday-text">{formatBatchDisplay(record.aging?.batch).title}</div>
                    <div className="text-xs text-monday-text-muted">{formatBatchDisplay(record.aging?.batch).subtitle}</div>
                  </div>
                  <PermissionButton
                    resource="agingMaintenance"
                    action="update"
                    onClick={() => openModal(record)}
                    variant="secondary"
                    className="!py-1 !px-3 !text-xs"
                  >
                    Edit
                  </PermissionButton>
                </div>
                <div className="flex flex-wrap gap-2 text-sm text-monday-text">
                  <div><span className="font-medium">Vessel:</span> {record.aging?.vessel ? formatVesselDisplay(record.aging.vessel) : record.vessel_type || 'Not available'}</div>
                  <div><span className="font-medium">Date:</span> {formatTimestamp(record.reading_timestamp)}</div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-monday-text-muted">
                  {/* Show a few key measurements inline, rest in expandable details */}
                  {hasMeasurement(record.ph) && (<div>pH: {record.ph}</div>)}
                  {hasMeasurement(record.ta) && (<div>TA: {record.ta}</div>)}
                  {hasMeasurement(record.so2) && (<div>SO2: {record.so2}</div>)}
                </div>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => toggleExpand(record.id)} className="flex-1 py-2 rounded bg-monday-header text-monday-text font-medium text-xs active:bg-monday-hover">{expandedRecords.includes(record.id) ? 'Hide Details' : 'Show Details'}</button>
                </div>
                {expandedRecords.includes(record.id) && (
                  <div className="mt-2 border-t pt-2 space-y-1 text-xs text-monday-text">
                    {hasMeasurement(record.aging_start_date) && (<div><span className="font-medium">Aging Start Date:</span> {new Intl.DateTimeFormat('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }).format(new Date(record.aging_start_date!))}</div>)}
                    {hasMeasurement(record.bisulfite) && (<div><span className="font-medium">Bisulfite:</span> {record.bisulfite}</div>)}
                    {hasMeasurement(record.color) && (<div><span className="font-medium">Color:</span> {record.color}</div>)}
                    {hasMeasurement(record.nose) && (<div><span className="font-medium">Nose:</span> {record.nose}</div>)}
                    {hasMeasurement(record.flavor) && (<div><span className="font-medium">Flavor:</span> {record.flavor}</div>)}
                    {record.topping !== undefined && record.topping !== null && (
                      <div>
                        <span className="font-medium">Topping:</span> {record.topping ? `Yes${record.topping_amount ? ` - ${record.topping_amount} liters` : ''}` : 'No'}
                      </div>
                    )}
                    {hasMeasurement(record.humidity) && (<div><span className="font-medium">Humidity:</span> {record.humidity}%</div>)}
                    {hasMeasurement(record.notes) && (<div><span className="font-medium">Notes:</span> {record.notes}</div>)}
                    <div className="flex gap-2 mt-2">
                      <button onClick={() => handleDelete(record.id)} className="flex-1 py-2 rounded bg-red-50 text-red-700 font-medium text-xs active:bg-red-100">Delete</button>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center text-monday-text-muted py-8">
              No maintenance records found. {' '}
              <button onClick={() => openModal()} className="text-primary-500 hover:text-primary-600">Add one now</button>
            </div>
          )}
        </div>
      </div>

      <MobileModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingRecord ? 'Edit Maintenance Record' : 'Add New Maintenance Record'}
        size="full"
      >
        <div className="mobile-form-container">
          <Formik
            key={editingRecord ? `edit-${editingRecord.id}` : 'new'}
            initialValues={{
              aging_id: editingRecord?.aging_id ?? selectedAgingId ?? null,
              reading_timestamp: editingRecord?.reading_timestamp
                ? toDateInputValue(editingRecord.reading_timestamp)
                : todayDateInputValue(),
              aging_start_date: editingRecord?.aging_start_date ?? null,
              ph: editingRecord?.ph ?? null,
              ta: editingRecord?.ta ?? null,
              color: editingRecord?.color ?? null,
              nose: editingRecord?.nose ?? null,
              bisulfite: editingRecord?.bisulfite ?? null,
              so2: editingRecord?.so2 ?? null,
              topping: editingRecord?.topping ?? null,
              topping_amount: editingRecord?.topping_amount ?? null,
              humidity: editingRecord?.humidity ?? null,
              flavor: editingRecord?.flavor ?? null,
              notes: editingRecord?.notes ?? ''
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, setFieldValue }) => (
              <Form className="space-y-4">
                <div>
                  <label htmlFor="aging_id" className="form-label mb-1">
                    Aging Process <span className="form-required-dot"></span>
                  </label>
                  <Field as="select" id="aging_id" name="aging_id" className="mobile-form-input">
                    <option value="">Select Aging Process</option>
                    {agingProcesses.map((aging) => (
                      <option key={aging.id} value={aging.id}>
                        {formatBatchDisplay(aging.batch).title} ({formatVesselDisplay(aging.vessel)})
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="aging_id" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="reading_timestamp" className="form-label mb-1">
                      Date <span className="form-required-dot"></span>
                    </label>
                    <Field
                      type="date"
                      id="reading_timestamp"
                      name="reading_timestamp"
                      className="mobile-form-input"
                    />
                    <p className="mt-1 text-xs text-monday-text-muted">
                      When this measurement was taken (defaults to today)
                    </p>
                    <ErrorMessage name="reading_timestamp" component="div" className="mt-1 text-sm text-red-600" />
                  </div>

                  <div>
                    <label htmlFor="aging_start_date" className="form-label mb-1">
                      Aging Start Date
                    </label>
                    <Field
                      type="date"
                      id="aging_start_date"
                      name="aging_start_date"
                      className="mobile-form-input"
                      placeholder="Select aging start date"
                    />
                    <ErrorMessage name="aging_start_date" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="ph" className="form-label mb-1">
                      pH
                    </label>
                    <Field type="number" step="0.01" id="ph" name="ph" className="mobile-form-input" placeholder="e.g. 3.45" />
                    <ErrorMessage name="ph" component="div" className="mt-1 text-sm text-red-600" />
                  </div>

                  <div>
                    <label htmlFor="ta" className="form-label mb-1">
                      TA
                    </label>
                    <Field type="number" step="0.1" id="ta" name="ta" className="mobile-form-input" placeholder="e.g. 6.2" />
                    <ErrorMessage name="ta" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div>
                  <label htmlFor="color" className="form-label mb-1">
                    Color
                  </label>
                  <Field type="text" id="color" name="color" className="mobile-form-input" placeholder="e.g. Deep Ruby, Pale Gold" />
                  <ErrorMessage name="color" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="nose" className="form-label mb-1">
                    Nose
                  </label>
                  <Field
                    as="textarea"
                    id="nose"
                    name="nose"
                    rows={2}
                    className="mobile-form-input resize-none"
                    placeholder="e.g. Cherry, oak, vanilla"
                  />
                  <ErrorMessage name="nose" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="bisulfite" className="form-label mb-1">
                      Bisulfite
                    </label>
                    <Field type="number" step="0.1" id="bisulfite" name="bisulfite" className="mobile-form-input" placeholder="e.g. 50" />
                    <ErrorMessage name="bisulfite" component="div" className="mt-1 text-sm text-red-600" />
                  </div>

                  <div>
                    <label htmlFor="so2" className="form-label mb-1">
                      SO2
                    </label>
                    <Field type="number" step="0.1" id="so2" name="so2" className="mobile-form-input" placeholder="e.g. 25" />
                    <ErrorMessage name="so2" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="topping" className="form-label mb-1">
                      Topping
                    </label>
                    <Field
                      as="select"
                      id="topping"
                      name="topping"
                      className="mobile-form-input"
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const value = e.target.value;
                        const boolValue = value === 'true' ? true : value === 'false' ? false : null;
                        setFieldValue('topping', boolValue);
                        setShowToppingAmount(boolValue === true);
                        if (boolValue === false || boolValue === null) {
                          setFieldValue('topping_amount', null);
                        }
                      }}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </Field>
                    <ErrorMessage name="topping" component="div" className="mt-1 text-sm text-red-600" />
                  </div>

                  {showToppingAmount && (
                    <div>
                      <label htmlFor="topping_amount" className="form-label mb-1">
                        Topping Amount (liters)
                      </label>
                      <Field
                        type="number"
                        step="0.1"
                        id="topping_amount"
                        name="topping_amount"
                        className="mobile-form-input"
                        placeholder="e.g. 10.5"
                      />
                      <ErrorMessage name="topping_amount" component="div" className="mt-1 text-sm text-red-600" />
                    </div>
                  )}

                  <div>
                    <label htmlFor="humidity" className="form-label mb-1">
                      Humidity (%)
                    </label>
                    <Field type="number" step="1" id="humidity" name="humidity" className="mobile-form-input" placeholder="e.g. 65" />
                    <ErrorMessage name="humidity" component="div" className="mt-1 text-sm text-red-600" />
                  </div>
                </div>

                <div>
                  <label htmlFor="flavor" className="form-label mb-1">
                    Flavor
                  </label>
                  <Field
                    as="textarea"
                    id="flavor"
                    name="flavor"
                    rows={2}
                    className="mobile-form-input resize-none"
                    placeholder="e.g. Rich berry notes with hints of chocolate"
                  />
                  <ErrorMessage name="flavor" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="notes" className="form-label mb-1">
                    Notes
                  </label>
                  <Field
                    as="textarea"
                    id="notes"
                    name="notes"
                    rows={3}
                    className="mobile-form-input resize-none"
                    placeholder="Optional notes about this maintenance record"
                  />
                  <ErrorMessage name="notes" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="mobile-btn mobile-btn-primary w-full sm:col-start-2 sm:text-sm"
                  >
                    {isSubmitting ? 'Saving...' : editingRecord ? 'Update' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mobile-btn mobile-btn-secondary mt-3 w-full sm:mt-0 sm:col-start-1 sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </MobileModal>
    </div>
  );
};

export default AgingMaintenance; 